import { DetailBox } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Col, Row } from 'reactstrap';

import GoBackButton from '../../../../components/buttons/GoBackButton';
import { useStepState } from '../../../../components/step/useStepState';
import { PathsLayouts } from '../../../../config/routes';
import { TranslationsKeys } from '../../../../locales/translations';
import { getAcademicOfferById } from '../api';
import { useAcademicOfferWizard } from '../context/AcademicOfferWizardContext';
import { NonStructuredAcademicOffer } from '../types';

export function AcademicOfferEditHeader() {
  const history = useHistory();
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const { ceAcademicOfferId, setOfferStatus } = useAcademicOfferWizard();
  const { currentStep, prevStep } = useStepState();
  const [offer, setOffer] = useState<NonStructuredAcademicOffer | null>(null);

  useEffect(() => {
    const loadOffer = async () => {
      if (!ceAcademicOfferId) {
        setOffer(null);
        return;
      }
      const { data } = await getAcademicOfferById(ceAcademicOfferId);
      if (data) {
        setOffer(data);
        setOfferStatus(data.status);
      }
    };

    loadOffer();
  }, [ceAcademicOfferId, setOfferStatus]);

  const handleGoBack = useCallback(() => {
    if (currentStep === 0) {
      history.push(`${PathsLayouts.nonStructured}/academic-offers`);
    } else {
      prevStep();
    }
  }, [currentStep, history, prevStep]);

  const detailColumns = useMemo(() => {
    if (!offer) {
      return [];
    }

    const firstOffer = offer.academicOfferStudyPlanVersionOffers?.[0];
    const studyPlanVersionOffer = firstOffer?.studyPlanVersionOffer;
    const studyPlan = studyPlanVersionOffer?.studyPlanVersion?.studyPlan;

    return [
      {
        title: t('header.status'),
        body: offer.status || '-',
      },
      {
        title: t('header.academicLevel', 'Nivel Académico'),
        body: studyPlan?.academicLevel?.name || '-',
      },
      {
        title: t('header.programName'),
        body: studyPlan?.name || '-',
      },
      {
        title: t('header.programCode'),
        body: studyPlan?.code || '-',
      },
      {
        title: t('header.modality'),
        body: studyPlan?.modality?.name || '-',
      },
      {
        title: t('header.school'),
        body: studyPlan?.school?.name || '-',
      },
      {
        title: t('header.campus'),
        body: studyPlanVersionOffer?.campus?.name || '-',
      },
    ];
  }, [offer, t]);

  return (
    <Row className="mb-4">
      <Col xs={12} sm={4}>
        <GoBackButton
          onClick={handleGoBack}
          text={currentStep === 0 ? t('actions.back') : t('actions.backstep')}
        />
      </Col>
      <Col xs={12} sm={8}>
        {offer && <DetailBox columns={detailColumns} />}
      </Col>
    </Row>
  );
}
